import { CoreBox, CoreTypography } from '@youscience/core';

import ActionButton from '@components/ActionButton';
import { getSchoolDetailsPath } from '@constants/ROUTES';
import { SchoolCardData } from '@interfaces/schoolCard';
import { getEnrollmentSize } from '@utils/schoolInfographics';

import { SchoolInfographicItem } from '@components/SchoolInfographicItem';
import { EllipsisTypography } from '@components/EllipsisTypography';
import { UtahBadge } from '@components/UtahBadge';

import LocationIcon from '@images/location.svg?react';
import HatIcon from '@images/hat.svg?react';
import EnrollmentSizeIcon from '@images/enrollement-size.svg?react';
import CostIcon from '@images/cost.svg?react';
import FilledHeartIcon from '@images/heart-filled.svg?react';
import HeartIcon from '@images/heart.svg?react';
import ChevronRightIcon from '@images/chevron-right.svg?react';
import NotesIcon from '@images/notes.svg?react';

import {
  sxStyles,
  StyledLinearSchoolCard,
  StyledInfographicContainer,
  StyledMoreLink,
  StyledNotes,
  StyledDistance,
} from './LinearSchoolCard.styles';

interface LinearSchoolCardProps {
  schoolCardData: SchoolCardData;
  onToggleAddToFavorites: () => void;
  isSchoolFavorite: boolean;
  onOpenSurveyNotes: () => void;
}

export const LinearSchoolCard = (props: LinearSchoolCardProps) => {
  const {
    schoolCardData: {
      id,
      title,
      addToFavoritesButtonId,
      address,
      type,
      enrollmentSize,
      costPerYear,
      cameFrom,
      isUshe,
      distanceLabel,
    },
    onToggleAddToFavorites,
    isSchoolFavorite,
    onOpenSurveyNotes,
  } = props;

  return (
    <StyledLinearSchoolCard>
      <CoreBox sx={sxStyles.schoolTitleWrap}>
        <EllipsisTypography
          data-testid="schoolTitle"
          stringCount={2}
          sx={sxStyles.schoolTitle}
          component="div"
          variant="h5"
        >
          {title}
        </EllipsisTypography>

        {isUshe && (
          <CoreBox sx={sxStyles.utahBadgeWrap}>
            <UtahBadge variant="ushe" />
          </CoreBox>
        )}

        <CoreBox sx={sxStyles.schoolAddressWrap}>
          <LocationIcon />

          <CoreTypography variant="caption">{address}</CoreTypography>

          {distanceLabel && (
            <StyledDistance data-testid="schoolDistance" variant="caption">
              {` · ${distanceLabel}`}
            </StyledDistance>
          )}
        </CoreBox>
      </CoreBox>

      <StyledInfographicContainer>
        <SchoolInfographicItem icon={<HatIcon />} title="School type" value={type} />

        <SchoolInfographicItem
          icon={<EnrollmentSizeIcon />}
          title="Enrollment size"
          value={getEnrollmentSize(enrollmentSize)}
        />

        <SchoolInfographicItem icon={<CostIcon />} title="Cost per year" value={costPerYear} />
      </StyledInfographicContainer>

      <CoreBox sx={sxStyles.actionsWrap}>
        {isSchoolFavorite && (
          <StyledNotes data-testid="notesIcon" onClick={onOpenSurveyNotes}>
            <NotesIcon />
          </StyledNotes>
        )}

        <ActionButton
          id={addToFavoritesButtonId}
          sx={sxStyles.favoriteBtn}
          variant="text"
          type="button"
          onClick={onToggleAddToFavorites}
        >
          {isSchoolFavorite ? <FilledHeartIcon /> : <HeartIcon />}
        </ActionButton>

        <StyledMoreLink to={getSchoolDetailsPath(id)} state={cameFrom}>
          <ChevronRightIcon />
        </StyledMoreLink>
      </CoreBox>
    </StyledLinearSchoolCard>
  );
};
